import { isBrowser } from '@jouzen/ecom-utils';
import {
  defaultShouldDehydrateQuery,
  type Query,
  QueryCache,
  QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { logger as browserLogger } from '@/logger/logger.browser';

export const getDefaultStaleTime = (): number => 1000 * 60 * 5;

/**
 * Query names whose key params are known to be free of PII/secrets and are
 * therefore safe to include in logs. Anything NOT listed here has its params
 * redacted before logging (secure by default) — several query keys carry PII
 * (e.g. `fetchSessionData` includes the user's email, `VerifyAddress` includes
 * the physical address), and this handler logs the key for every query in the
 * app.
 */
const PARAM_SAFE_QUERIES = new Set([
  'useProductSelectionOptions',
  'useProductsData',
  'validate-redeem-code',
]);

/**
 * Redact a query key for logging. The first element (the query name) is always
 * a safe identifier and is kept; the remaining params are dropped unless the
 * query is explicitly allow-listed as param-safe. This keeps useful context
 * (e.g. the SKU on product queries) without ever leaking PII/secrets.
 */
export const sanitizeQueryKey = (queryKey: QueryKey): unknown[] => {
  const [name, ...params] = queryKey;
  if (typeof name === 'string' && PARAM_SAFE_QUERIES.has(name)) {
    return [...queryKey];
  }
  return params.length ? [name, '[redacted]'] : [name];
};

export const shouldDehydrateQuery = (
  query: Query<unknown, Error, unknown, QueryKey>,
): boolean =>
  defaultShouldDehydrateQuery(query) || query.state.status === 'pending';

export const makeQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: getDefaultStaleTime(),
      },
      dehydrate: {
        // include pending queries in dehydration
        shouldDehydrateQuery,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        browserLogger.warn('Error fetching data with react-query', {
          error,
          // Query fns may rethrow a wrapper Error whose real failure (e.g. the
          // HTTP status) lives in `cause`; surface it since the DD SDK doesn't
          // serialize Error.cause on its own.
          cause: error.cause,
          // Sanitize before logging — keys can carry PII/secrets in their
          // params. `queryHash` (the stringified key) is intentionally NOT
          // logged, as it would leak the same unredacted params.
          queryKey: sanitizeQueryKey(query.queryKey),
        });
      },
    }),
  });

let browserQueryClient: QueryClient | undefined = undefined;

export const getQueryClient = () => {
  if (!isBrowser()) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
};
